import { AddOutlined, ArrowBackOutlined, BlockOutlined, DeleteOutline, EditOutlined,
  DevicesOutlined, LockOutlined, LockResetOutlined, ManageAccountsOutlined, SaveOutlined
} from "@mui/icons-material";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Grid, List, ListItemButton,
  ListItemText, Paper, Stack, Tab, Tabs, TextField, Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AccessRole, AccessUser, assignAccessRoles, createAccessRole,
  createAccessUser, disableAccessUser, loadAccessPermissions, loadAccessRoles,
  loadAccessUsers, reactivateAccessUser, resetAccessUserPassword,
  suspendAccessUser, updateAccessRole, updateAccessUser, loadActiveUserSessions
} from "./api";

const fallbackResourceLabel = (value: string) => value.replaceAll("_", " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

interface Props { onBack: () => void; permissions: string[]; }
export function AccessManagementPage({ onBack, permissions: currentPermissions }: Props) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [tab, setTab] = useState(0);
  const [selectedRoleId, setSelectedRoleId] = useState<string>();
  const [newOpen, setNewOpen] = useState(false);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newCode, setNewCode] = useState(""); const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const roles = useQuery({ queryKey: ["access-roles"], queryFn: loadAccessRoles });
  const rights = useQuery({ queryKey: ["access-permissions"], queryFn: loadAccessPermissions });
  const users = useQuery({ queryKey: ["access-users"], queryFn: loadAccessUsers });
  const sessions = useQuery({ queryKey: ["access-sessions"], queryFn: loadActiveUserSessions,
    enabled: tab === 2, refetchInterval: 30_000 });
  const selected = roles.data?.find((role) => role.id === selectedRoleId) ?? roles.data?.[0];
  const canManageRoles = currentPermissions.includes("role:manage");
  const canManageUsers = currentPermissions.includes("user:manage");
  useEffect(() => { if (!selectedRoleId && roles.data?.[0]) setSelectedRoleId(roles.data[0].id); }, [roles.data, selectedRoleId]);
  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: ["access-roles"] }),
    client.invalidateQueries({ queryKey: ["access-users"] }),
    client.invalidateQueries({ queryKey: ["current-user"] })
  ]);
  const createRole = useMutation({ mutationFn: createAccessRole, onSuccess: async (role) => {
    await refresh(); setSelectedRoleId(role.id); setNewOpen(false);
  }});

  return <Stack spacing={2} sx={{ pb: 3 }}>
    <Paper sx={{ p: 2, borderTop: 3, borderColor: "secondary.main" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<ArrowBackOutlined />} onClick={onBack}>{t("common.home")}</Button>
          <Box><Typography variant="h4" fontWeight={800}>{t("administration.access.title")}</Typography>
            <Typography color="text.secondary">{t("administration.access.subtitle")}</Typography></Box>
        </Stack>
        {tab === 0 && canManageRoles && <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setNewOpen(true)}>{t("administration.access.newPersona")}</Button>}
        {tab === 1 && canManageUsers && <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setNewUserOpen(true)}>{t("administration.access.newUser")}</Button>}
      </Stack>
    </Paper>
    <Tabs value={tab} onChange={(_, value) => setTab(value)}>
      <Tab label={`${t("administration.access.tabs.personas")} (${roles.data?.length ?? 0})`} />
      <Tab label={`${t("administration.access.tabs.users")} (${users.data?.length ?? 0})`} />
      <Tab label={`${t("administration.access.tabs.connected")} (${sessions.data?.length ?? 0})`} />
    </Tabs>
    {(roles.isError || rights.isError || users.isError) && <Alert severity="error">{t("administration.access.loadError")}</Alert>}
    {tab === 0 ? <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}><Paper variant="outlined"><List disablePadding>
        {(roles.data ?? []).map((role) => <ListItemButton key={role.id} selected={selected?.id === role.id} onClick={() => setSelectedRoleId(role.id)} divider>
          <ListItemText primary={<Stack direction="row" spacing={1} alignItems="center"><Typography fontWeight={800}>{role.displayName}</Typography>{role.systemRole && <LockOutlined fontSize="small" />}</Stack>}
            secondary={t("administration.access.rights", { count: role.permissionCodes.length, users: role.assignedUsers })} />
          <Chip size="small" label={t(role.status === "ACTIVE" ? "administration.access.active" : "administration.access.inactive")} color={role.status === "ACTIVE" ? "success" : "default"} />
        </ListItemButton>)}</List></Paper></Grid>
      <Grid size={{ xs: 12, md: 8 }}>{selected && <RoleEditor role={selected} allPermissions={rights.data ?? []}
        readOnly={!canManageRoles || selected.systemRole} onSaved={refresh} />}</Grid>
    </Grid> : tab === 1 ? <UserAssignments users={users.data ?? []} roles={roles.data ?? []}
      canManage={canManageUsers} onSaved={refresh} newOpen={newUserOpen}
      onCloseNew={() => setNewUserOpen(false)} /> : <ActiveSessions
        sessions={sessions.data ?? []} loading={sessions.isLoading}
        error={sessions.isError} />}

    <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="sm"><DialogTitle>{t("administration.access.newPersona")}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}><Alert severity="info">{t("administration.access.createPersonaHint")}</Alert>
        <TextField label={t("administration.access.code")} value={newCode} onChange={(e) => setNewCode(e.target.value)} helperText={t("administration.access.codeExample")} />
        <TextField label={t("administration.access.name")} value={newName} onChange={(e) => setNewName(e.target.value)} />
        <TextField label={t("administration.access.description")} multiline minRows={2} value={newDescription} onChange={(e) => setNewDescription(e.target.value)} /></Stack></DialogContent>
      <DialogActions><Button onClick={() => setNewOpen(false)}>{t("common.actions.cancel")}</Button><Button variant="contained" disabled={!newCode.trim() || !newName.trim() || createRole.isPending}
        onClick={() => createRole.mutate({ code: newCode, displayName: newName, description: newDescription, permissionCodes: [] })}>{t("administration.access.create")}</Button></DialogActions></Dialog>
  </Stack>;
}

function ActiveSessions({ sessions, loading, error }: {
  sessions: Awaited<ReturnType<typeof loadActiveUserSessions>>;
  loading: boolean; error: boolean;
}) {
  const { t, i18n } = useTranslation();
  const format = (value: string) => new Intl.DateTimeFormat(i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR", {
    dateStyle: "short", timeStyle: "medium"
  }).format(new Date(value));
  return <Stack spacing={1.5}>
    <Alert severity="info">{t("administration.access.sessions.info")}</Alert>
    {loading && <Typography color="text.secondary">{t("administration.access.sessions.loading")}</Typography>}
    {error && <Alert severity="error">{t("administration.access.sessions.error")}</Alert>}
    {!loading && !error && sessions.length === 0 && <Alert severity="success">{t("administration.access.sessions.empty")}</Alert>}
    {sessions.map((session) => <Paper key={session.sessionId} variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} gap={2}
        justifyContent="space-between" alignItems={{ sm: "center" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <DevicesOutlined color="primary" />
          <Box><Typography fontWeight={800}>{session.username}</Typography>
            <Typography color="text.secondary">{t("administration.access.sessions.ip", { value: session.ipAddress || t("administration.access.sessions.unavailable") })}</Typography>
            <Typography color="text.secondary">
              {session.deviceName ?? t("administration.access.sessions.unknownDevice")}
              {session.deviceType ? ` · ${session.deviceType}` : ""}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ minWidth: { sm: 280 } }}>
          <Typography variant="body2"><strong>{t("administration.access.sessions.browser")}</strong>{" "}
            {session.browserName
              ? `${session.browserName}${session.browserVersion ? ` ${session.browserVersion}` : ""}`
              : t("administration.access.sessions.pendingSignal")}
          </Typography>
          <Typography variant="body2"><strong>{t("administration.access.sessions.system")}</strong>{" "}
            {session.operatingSystem ?? t("administration.access.sessions.unavailable")}
          </Typography>
          <Typography variant="body2"><strong>{t("administration.access.sessions.connectedSince")}</strong> {format(session.startedAt)}</Typography>
          <Typography variant="body2" color="text.secondary"><strong>{t("administration.access.sessions.lastActivity")}</strong> {format(session.lastAccessAt)}</Typography>
        </Box>
      </Stack>
    </Paper>)}
  </Stack>;
}

function RoleEditor({ role, allPermissions, readOnly, onSaved }: { role: AccessRole; allPermissions: Awaited<ReturnType<typeof loadAccessPermissions>>; readOnly: boolean; onSaved: () => Promise<unknown>; }) {
  const { t } = useTranslation();
  const [name, setName] = useState(role.displayName); const [description, setDescription] = useState(role.description ?? "");
  const [selected, setSelected] = useState<string[]>(role.permissionCodes);
  useEffect(() => { setName(role.displayName); setDescription(role.description ?? ""); setSelected(role.permissionCodes); }, [role]);
  const groups = useMemo(() => allPermissions.reduce((result, right) => {
    const existing = result.get(right.resource) ?? [];
    existing.push(right);
    result.set(right.resource, existing);
    return result;
  }, new Map<string, typeof allPermissions>()), [allPermissions]);
  const save = useMutation({ mutationFn: () => updateAccessRole(role, { displayName: name, description, status: role.status, permissionCodes: selected }), onSuccess: onSaved });
  return <Paper variant="outlined" sx={{ p: 2 }}><Stack spacing={2}>
    <Stack direction="row" justifyContent="space-between"><Box><Typography variant="h5" fontWeight={800}>{role.displayName}</Typography><Typography variant="body2" color="text.secondary">{role.code}</Typography></Box>
      {role.systemRole && <Chip icon={<LockOutlined />} label={t("administration.access.role.protected")} />}</Stack>
    <TextField label={t("administration.access.role.name")} value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} />
    <TextField label={t("administration.access.role.responsibility")} multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} disabled={readOnly} />
    <Divider /><Typography variant="h6" fontWeight={800}>{t("administration.access.role.permissions")}</Typography>
    <Alert severity={readOnly ? "info" : "warning"}>{t(readOnly ? "administration.access.role.readonly" : "administration.access.role.warning")}</Alert>
    {[...groups.entries()].map(([resource, items]) => <Box key={resource}><Typography fontWeight={800}>{resource === "flow_diagnostic" ? t("administration.access.role.flowDiagnostic") : fallbackResourceLabel(resource)}</Typography>
      <Grid container>{items.map((right) => <Grid key={right.code} size={{ xs: 12, sm: 6 }}><FormControlLabel disabled={readOnly}
        control={<Checkbox checked={selected.includes(right.code)} onChange={(_, checked) => setSelected((current) => checked ? [...current, right.code] : current.filter((code) => code !== right.code))} />}
        label={`${t(`administration.access.actions.${right.action}`, { defaultValue: right.action })} (${right.code})`} /></Grid>)}</Grid></Box>)}
    {!readOnly && <Button variant="contained" startIcon={<SaveOutlined />} onClick={() => save.mutate()} disabled={save.isPending}>{t("administration.access.role.save")}</Button>}
  </Stack></Paper>;
}

function UserAssignments({ users, roles, canManage, onSaved, newOpen, onCloseNew }: {
  users: AccessUser[]; roles: AccessRole[]; canManage: boolean;
  onSaved: () => Promise<unknown>; newOpen: boolean; onCloseNew: () => void;
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState<AccessUser>();
  const [selected, setSelected] = useState<string[]>([]);
  const [action, setAction] = useState<{ type: "suspend" | "disable" | "password"; user: AccessUser }>();
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [forceChange, setForceChange] = useState(true);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [initialPassword, setInitialPassword] = useState("");
  const [newForceChange, setNewForceChange] = useState(true);
  const [newRoles, setNewRoles] = useState<string[]>([]);
  const [editUser, setEditUser] = useState<AccessUser>();
  const [editEmail, setEditEmail] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");

  const closeAction = () => { setAction(undefined); setReason(""); setPassword(""); };
  const roleMutation = useMutation({
    mutationFn: () => assignAccessRoles(active!.id, selected),
    onSuccess: async () => { await onSaved(); setActive(undefined); }
  });
  const createMutation = useMutation({
    mutationFn: () => createAccessUser({ username, email, firstName, lastName,
      initialPassword, forcePasswordChange: newForceChange, roleIds: newRoles }),
    onSuccess: async () => {
      await onSaved(); onCloseNew(); setUsername(""); setEmail("");
      setFirstName(""); setLastName(""); setInitialPassword(""); setNewRoles([]);
    }
  });
  const editMutation = useMutation({
    mutationFn: () => updateAccessUser(editUser!, {
      email: editEmail, firstName: editFirstName, lastName: editLastName
    }),
    onSuccess: async () => { await onSaved(); setEditUser(undefined); }
  });
  const actionMutation = useMutation({
    mutationFn: async () => {
      if (!action) throw new Error("Action absente.");
      if (action.type === "suspend") await suspendAccessUser(action.user, reason);
      else if (action.type === "disable") await disableAccessUser(action.user, reason);
      else await resetAccessUserPassword(action.user, password, forceChange);
    },
    onSuccess: async () => { await onSaved(); closeAction(); }
  });
  const reactivate = useMutation({
    mutationFn: reactivateAccessUser,
    onSuccess: onSaved
  });
  const error = roleMutation.error ?? createMutation.error ?? editMutation.error ?? actionMutation.error
    ?? reactivate.error;
  const activeRoles = roles.filter((role) => role.status === "ACTIVE");

  return <Stack spacing={1.5}>
    <Alert severity="info">{t("administration.access.usersInfo")}</Alert>
    {error && <Alert severity="error">{error instanceof Error ? error.message : t("administration.access.actionImpossible")}</Alert>}
    {users.map((user) => <Paper key={user.id} variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography fontWeight={800}>{user.displayName}</Typography>
            <Chip size="small" label={t(user.status === "ACTIVE" ? "administration.access.statuses.active"
              : user.status === "SUSPENDED" ? "administration.access.statuses.suspended" : "administration.access.statuses.disabled")}
              color={user.status === "ACTIVE" ? "success"
                : user.status === "SUSPENDED" ? "warning" : "default"} />
          </Stack>
          <Typography color="text.secondary">
            {user.username ? `${user.username} · ` : ""}{user.email}
          </Typography>
          {user.disabledReason && <Typography variant="body2" color="text.secondary">
            {t("administration.access.reason", { reason: user.disabledReason })}
          </Typography>}
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {user.roleIds.map((id) => <Chip key={id} size="small"
              label={roles.find((role) => role.id === id)?.displayName ?? id} />)}
          </Stack>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="flex-end"
          alignItems="center" sx={{ alignSelf: "center", "& .MuiButton-root": {
            minHeight: 40, px: 1.5, whiteSpace: "nowrap"
          } }}>
          <Button startIcon={<EditOutlined />} variant="outlined" size="small"
            disabled={!canManage || user.status === "DISABLED"}
            onClick={() => {
              setEditUser(user); setEditEmail(user.email);
              setEditFirstName(user.firstName ?? ""); setEditLastName(user.lastName ?? "");
            }}>
            {t("administration.access.buttons.edit")}
          </Button>
          <Button startIcon={<ManageAccountsOutlined />} variant="outlined"
            size="small"
            disabled={!canManage || user.status === "DISABLED"}
            onClick={() => { setActive(user); setSelected(user.roleIds); }}>
            {t("administration.access.buttons.personas")}
          </Button>
          <Button startIcon={<LockResetOutlined />} variant="outlined"
            size="small"
            disabled={!canManage || user.status === "DISABLED"}
            onClick={() => { setAction({ type: "password", user }); setForceChange(true); }}>
            {t("administration.access.buttons.password")}
          </Button>
          {user.status === "ACTIVE" ? <Button startIcon={<BlockOutlined />}
            color="warning" variant="outlined" size="small" disabled={!canManage}
            onClick={() => setAction({ type: "suspend", user })}>{t("administration.access.buttons.suspend")}</Button>
            : user.status === "SUSPENDED" ? <Button variant="outlined" size="small"
              disabled={!canManage || reactivate.isPending}
              onClick={() => reactivate.mutate(user)}>{t("administration.access.buttons.reactivate")}</Button> : null}
          {user.status !== "DISABLED" && <Button startIcon={<DeleteOutline />}
            color="error" variant="outlined" size="small" disabled={!canManage}
            onClick={() => setAction({ type: "disable", user })}>
            {t("administration.access.buttons.logicalDelete")}
          </Button>}
        </Stack>
      </Stack>
    </Paper>)}

    <Dialog open={newOpen} onClose={onCloseNew} fullWidth maxWidth="md">
      <DialogTitle>{t("administration.access.userCreate.title")}</DialogTitle>
      <DialogContent dividers><Stack spacing={2}>
        <Alert severity="info">{t("administration.access.userCreate.info")}</Alert>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth required label={t("administration.access.userCreate.username")}
            value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())}
            slotProps={{ htmlInput: { autoCapitalize: "none", spellCheck: false } }}
            helperText={t("administration.access.userCreate.lowercase")} /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth required type="email" label={t("administration.access.userCreate.email")}
            value={email} onChange={(e) => setEmail(e.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth required label={t("administration.access.userCreate.firstName")}
            value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth required label={t("administration.access.userCreate.lastName")}
            value={lastName} onChange={(e) => setLastName(e.target.value)} /></Grid>
        </Grid>
        <TextField required type="password" label={t("administration.access.userCreate.initialPassword")}
          value={initialPassword} onChange={(e) => setInitialPassword(e.target.value)}
          helperText={t("administration.access.userCreate.passwordHint")} />
        <FormControlLabel control={<Checkbox checked={newForceChange}
          onChange={(_, checked) => setNewForceChange(checked)} />}
          label={t("administration.access.userCreate.force")} />
        <Divider /><Typography fontWeight={800}>{t("administration.access.userCreate.roles")}</Typography>
        {activeRoles.map((role) => <FormControlLabel key={role.id}
          control={<Checkbox checked={newRoles.includes(role.id)}
            onChange={(_, checked) => setNewRoles((current) => checked
              ? [...current, role.id] : current.filter((id) => id !== role.id))} />}
          label={role.displayName} />)}
        {createMutation.isError && <Alert severity="error">
          {createMutation.error instanceof Error ? createMutation.error.message
            : t("administration.access.userCreate.error")}
        </Alert>}
      </Stack></DialogContent>
      <DialogActions><Button onClick={onCloseNew}>{t("common.actions.cancel")}</Button>
        <Button variant="contained" disabled={createMutation.isPending
          || !username.trim() || !email.trim() || !firstName.trim()
          || !lastName.trim() || initialPassword.length < 8}
          onClick={() => createMutation.mutate()}>{t("administration.access.userCreate.submit")}</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(editUser)} onClose={() => setEditUser(undefined)} fullWidth maxWidth="sm">
      <DialogTitle>{t("administration.access.userEdit.title")}</DialogTitle>
      <DialogContent dividers><Stack spacing={2}>
        <TextField label={t("administration.access.userCreate.username")} value={editUser?.username ?? ""} disabled
          helperText={t("administration.access.userEdit.immutable")} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth required label={t("administration.access.userCreate.firstName")}
            value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth required label={t("administration.access.userCreate.lastName")}
            value={editLastName} onChange={(e) => setEditLastName(e.target.value)} /></Grid>
        </Grid>
        <TextField fullWidth required type="email" label={t("administration.access.userCreate.email")}
          value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
        {editMutation.isError && <Alert severity="error">
          {editMutation.error instanceof Error ? editMutation.error.message : t("administration.access.userEdit.error")}
        </Alert>}
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setEditUser(undefined)}>{t("common.actions.cancel")}</Button>
        <Button variant="contained" disabled={editMutation.isPending
          || !editEmail.trim() || !editFirstName.trim() || !editLastName.trim()}
          onClick={() => editMutation.mutate()}>{t("common.actions.save", { defaultValue: "Enregistrer" })}</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(active)} onClose={() => setActive(undefined)} fullWidth maxWidth="sm">
      <DialogTitle>{t("administration.access.assignment.title", { name: active?.displayName })}</DialogTitle><DialogContent>
        <Stack>{activeRoles.map((role) => <FormControlLabel key={role.id}
          control={<Checkbox checked={selected.includes(role.id)}
            onChange={(_, checked) => setSelected((current) => checked
              ? [...current, role.id] : current.filter((id) => id !== role.id))} />}
          label={<Box><Typography fontWeight={700}>{role.displayName}</Typography>
            <Typography variant="caption">{role.description}</Typography></Box>} />)}</Stack>
      </DialogContent><DialogActions><Button onClick={() => setActive(undefined)}>{t("common.actions.cancel")}</Button>
        <Button variant="contained" onClick={() => roleMutation.mutate()}
          disabled={roleMutation.isPending}>{t("administration.access.assignment.save")}</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(action)} onClose={closeAction} fullWidth maxWidth="sm">
      <DialogTitle>{t(action?.type === "password" ? "administration.access.userAction.resetTitle"
        : action?.type === "suspend" ? "administration.access.userAction.suspendTitle"
          : "administration.access.userAction.deleteTitle")}</DialogTitle>
      <DialogContent dividers><Stack spacing={2}>
        <Typography>{action?.user.displayName} · {action?.user.email}</Typography>
        {action?.type === "password" ? <>
          <TextField required type="password" label={t("administration.access.userAction.newPassword")}
            value={password} onChange={(e) => setPassword(e.target.value)} />
          <FormControlLabel control={<Checkbox checked={forceChange}
            onChange={(_, checked) => setForceChange(checked)} />}
            label={t("administration.access.userAction.force")} />
        </> : <>
          <Alert severity={action?.type === "disable" ? "error" : "warning"}>
            {action?.type === "disable"
              ? t("administration.access.userAction.deleteWarning")
              : t("administration.access.userAction.suspendWarning")}
          </Alert>
          <TextField required multiline minRows={2} label={t("administration.access.userAction.reason")}
            value={reason} onChange={(e) => setReason(e.target.value)} />
        </>}
        {actionMutation.isError && <Alert severity="error">
          {actionMutation.error instanceof Error ? actionMutation.error.message : t("administration.access.actionImpossible")}
        </Alert>}
      </Stack></DialogContent>
      <DialogActions><Button onClick={closeAction}>{t("common.actions.cancel")}</Button>
        <Button variant="contained" color={action?.type === "disable" ? "error" : "primary"}
          disabled={actionMutation.isPending || (action?.type === "password"
            ? password.length < 8 : reason.trim().length < 3)}
          onClick={() => actionMutation.mutate()}>{t("administration.access.userAction.confirm")}</Button></DialogActions>
    </Dialog>
  </Stack>;
}
